import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import DataObject from "@mui/icons-material/DataObject";
import BentoOutlined from "@mui/icons-material/BentoOutlined";
import { useMsal } from "@azure/msal-react";
import Loading from "../components/loading";
import Dialogbox from "../components/dialogs/dialogBox";
import Breadcrumbs from "../components/breadcrumbs";
import CodeEditor from "../components/monacoEditor";
import { useSecrets } from "../components/hooks/useSecrets";
import { useApiHeaders } from "../components/hooks/useApiHeaders";
import {
  updateTagsWithUser,
  validateJson,
} from "../components/utils/tagHelpers";
import { config } from "../components/config";
import useAccessToken from "../auth/accessPermission";
import useUserAccount from "../components/hooks/userAccountInfo";

const commonStyles = {
  bgcolor: "background.paper",
  m: 1,
  border: 1,
  width: "60rem",
  height: "39rem",
};

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  minHeight: "100vh",
};

function UpdateSecretForm() {
  const [secret, setSecret] = useState("");
  const [initialValue, setInitialValue] = useState("");
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openWarning, setOpenWarning] = useState(false);
  const [openBCWarning, setOpenBCWarning] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isAppsettings, setIsAppsettings] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [tags, setTags] = useState([]);
  const [openError, setOpenError] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [updating, setUpdating] = useState(false);

  const navigate = useNavigate();
  const { instance } = useMsal();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const platformName = searchParams.get("platformName");
  const selectedCluster = searchParams.get("selectedCluster");
  const vaultId = searchParams.get("vaultName");
  const { secretName } = useParams();

  const { project, userRole, nameParts } = useUserAccount(instance);
  const accessToken = useAccessToken([config.apiAccess]);
  const { loading, fetchSecret, updateSecret } = useSecrets();

  const headers = useApiHeaders(
    accessToken,
    userRole,
    project,
    platformName,
    vaultId
  );

  const loadSecret = useCallback(async () => {
    if (userRole && headers && accessToken) {
      try {
        const response = await fetchSecret(secretName, platformName, headers);
        const secretData = response.secretValue;
        const tagData = response.tags || [];
        setTags(tagData);

        if (Object.keys(secretData)[0] === "appsettings.json") {
          setIsAppsettings(true);
          setInitialValue(
            JSON.stringify(secretData["appsettings.json"], null, 2)
          );
        } else {
          setInitialValue(JSON.stringify(secretData, null, 2));
        }
      } catch (error) {
        console.error("Error fetching secret:", error);
        setErrorMessage("Failed to load secret");
        setOpenError(true);
      }
    }
  }, [secretName, platformName, headers, userRole, accessToken, fetchSecret]);

  useEffect(() => {
    loadSecret();
  }, [loadSecret]);

  const handleSuccessAlertClose = useCallback(() => {
    setOpenSuccess(false);
    navigate(
      `/view-secret/${secretName}?platformName=${platformName}&selectedCluster=${selectedCluster}&vaultName=${vaultId}`
    );
  }, [navigate, secretName, platformName, selectedCluster, vaultId]);

  const handleWarningAlertClose = useCallback(() => {
    setOpenWarning(false);
    navigate(
      `/view-secret/${secretName}?platformName=${platformName}&selectedCluster=${selectedCluster}&vaultName=${vaultId}`
    );
  }, [navigate, secretName, platformName, selectedCluster, vaultId]);

  const handleCodeMirrorChange = useCallback(
    (value) => {
      setSecret(value);
      setIsModified(
        value.replace(/\s/g, "") !== initialValue.replace(/\s/g, "")
      );
    },
    [initialValue]
  );

  const handleUpdateSecret = useCallback(async () => {
    if (secret && isModified) {
      if (!validateJson(secret)) {
        setErrorMessage("Syntax error please check your input");
        setOpenError(true);
        return;
      }

      try {
        setUpdating(true);
        const updatedSecret = isAppsettings
          ? `{"appsettings.json": ${secret}}`
          : secret;

        const newTags = updateTagsWithUser(tags, nameParts, platformName);
        const payload = {
          secretValue: updatedSecret,
          tags: newTags,
        };

        const response = await updateSecret(
          secretName,
          payload,
          platformName,
          headers
        );
        if (response.status === 200) {
          setOpenSuccess(true);
        } else {
          setErrorMessage("Error updating secret");
          setOpenError(true);
        }
      } catch (error) {
        const backendError = error.response?.data?.error || "Unknown error";
        setErrorMessage(backendError);
        setOpenError(true);
      } finally {
        setUpdating(false);
      }
    } else {
      setWarningMessage("No changes were made!");
      setOpenWarning(true);
    }
  }, [
    secret,
    isModified,
    isAppsettings,
    tags,
    nameParts,
    platformName,
    secretName,
    updateSecret,
    headers,
  ]);

  const handleBCWarningAlertClose = useCallback(() => {
    setOpenBCWarning(false);
    navigate(
      `/view-secret/${secretName}?platformName=${platformName}&selectedCluster=${selectedCluster}&vaultName=${vaultId}`
    );
  }, [navigate, selectedCluster]);

  const handleOpenBCWarning = useCallback(() => {
    setOpenBCWarning(true);
    setWarningMessage("Changes not saved!");
  }, []);

  const handleOpenWarning = useCallback(() => {
    setOpenWarning(true);
    setWarningMessage("Changes not saved!");
  }, []);

  const handleDialogClose = useCallback(() => {
    setOpenError(false);
    setOpenWarning(false);
    setOpenBCWarning(false);
    setOpenSuccess(false);
  }, []);

  return (
    <div>
      <div style={{ paddingLeft: "2rem", marginBottom: "1rem" }}>
        <Breadcrumbs onIsModified={handleOpenBCWarning} isChange={isModified} />
      </div>

      {/* Skeleton loader only for initial fetch */}
      {loading && !initialValue ? (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Loading
            open={loading}
            type="skeleton"
            variant="code"
            rows={32}
            itemHeight={800}
            codebgcolor="grey.700"
            codecolor="grey.600"
          />
        </div>
      ) : (
        <div style={containerStyle}>
          <Typography
            variant="h3"
            gutterBottom
            style={{ marginTop: 20, color: "#1976D2" }}
          >
            Update Secret
          </Typography>

          <div style={{ width: "60rem" }}>
            <Box sx={{ width: "100%" }}>
              <Dialogbox
                open={openSuccess}
                onClose={handleSuccessAlertClose}
                title="Success"
                severity="success"
                message={
                  <div>
                    {secretName} updated successfully! <br />
                    Click on the close button to view secret
                  </div>
                }
              />

              <Dialogbox
                open={openWarning}
                onClose={handleDialogClose}
                title="Warning"
                severity="warning"
                message={
                  <div>
                    {warningMessage} <br />
                    Click on the button to continue editing or discard
                  </div>
                }
                onConfirm={handleBCWarningAlertClose}
                confirmText="Discard"
                closeText="Back to editing"
              />

              <Dialogbox
                open={openError}
                onClose={handleDialogClose}
                title="Error"
                severity="error"
                message={
                  <div>
                    Failed to update! <br />
                    Message: {errorMessage}
                  </div>
                }
              />
            </Box>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <p
                style={{
                  marginBottom: 3,
                  display: "flex",
                  alignItems: "center",
                  color: "#886701",
                  fontSize: 20,
                }}
              >
                <DataObject sx={{ paddingRight: 1 }} />
                {secretName}
              </p>
              <p
                style={{
                  marginBottom: 3,
                  display: "flex",
                  alignItems: "center",
                  color: "#886701",
                  fontSize: 20,
                }}
              >
                <BentoOutlined sx={{ paddingRight: 1 }} />
                {selectedCluster} Cluster
              </p>
            </div>

            <Box
              sx={{
                ...commonStyles,
                flexDirection: "column",
                borderColor: "grey.300",
                justifyContent: "space-between",
                display: "flex",
                borderRadius: "1%",
                height: "auto",
              }}
            >
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Box
                  component="div"
                  sx={{
                    my: 2,
                    p: 1,
                    bgcolor: "grey.100",
                    border: "1px solid",
                    borderColor: "grey.300",
                    borderRadius: 2,
                    width: "55rem",
                    height: "40rem",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <div style={{ overflow: "auto" }}>
                    <CodeEditor
                      language="json"
                      value={initialValue}
                      onChange={handleCodeMirrorChange}
                      selectedTheme="vs-dark"
                      readOnly="false"
                      showMinimap="true"
                    />
                  </div>
                </Box>
              </div>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  height: "2.5rem",
                  bgcolor: "grey.100",
                  borderColor: "grey.300",
                  textTransform: "capitalize",
                }}
              >
                <Button
                  sx={{ textTransform: "capitalize", px: 3 }}
                  onClick={handleUpdateSecret}
                >
                  update
                </Button>
                <Button
                  variant="neutral"
                  sx={{ textTransform: "capitalize", px: 3, ml: 2, mr: 5 }}
                  onClick={handleOpenWarning}
                >
                  cancel
                </Button>
              </Box>
            </Box>
          </div>
        </div>
      )}

      {/* Spinner overlay only when updating */}
      <Backdrop
        sx={{
          color: "#1976D2",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "transparent",
        }}
        open={updating}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
}

export default UpdateSecretForm;